import type {ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';

import CSSModules from 'react-css-modules';
import Div100vh from 'react-div-100vh';

import {useIsMobile} from '../../hooks/isMobile';
import Heading from '../../TextComponents/Heading';
import Text from '../../TextComponents/Text';
import type {TextColours} from '../../TextComponents/textUtils';
import {focusRefChild} from '../../utils/accessibilityUtils';
import ImageQuizImage from '../ImageQuizImage';

import styles from './styles.module.scss';

type InteractiveActivityProps = {
  children: ReactNode;
  instruction?: string;
  shouldAnimate: boolean;
  editMode?: boolean;
  onReplay?: () => void;
  replayButtonText?: string;
  videoThumbnail?: string;
  imageUrl?: string;
  thumbnailImageUrl?: string;
  disabled?: boolean;
  reskinClassName?: string;
  isPreview?: boolean;
  textColour?: TextColours;
  question: string;
  quizQuestionsContainImages: boolean;
  quizType?: string;
  questionType?: string;
  questionIndex?: number;
};

// eslint-disable-next-line complexity
const InteractiveActivity = ({
  editMode,
  onReplay,
  replayButtonText,
  instruction,
  videoThumbnail,
  imageUrl,
  thumbnailImageUrl,
  shouldAnimate = true,
  disabled,
  reskinClassName = '',
  children,
  isPreview = false,
  textColour = 'white',
  question,
  quizType,
  questionType,
  quizQuestionsContainImages = false,
  questionIndex,
}: InteractiveActivityProps): JSX.Element => {
  const isMobile = useIsMobile();
  const questionContainerRef = useRef<HTMLDivElement>(null);

  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    focusRefChild(questionContainerRef);
  }, [questionIndex]);

  useEffect(() => {
    window.setTimeout(() => {
      setHasAnimated(true);
    }, 5000);
  }, []);

  const isOrderingQuestion = questionType === 'ordering';

  let className = `interactive-activity ${reskinClassName}`;
  if (shouldAnimate && !hasAnimated) {
    className += ' show-animations';
  }
  if (!onReplay) {
    className += ' no-button';
  }
  if (isOrderingQuestion) {
    className += ' ordering';
  }
  if (editMode) {
    className += ' mobile-style';
  }
  if (isPreview) {
    className += ' preview-mode';
  }

  const showVideoThumbnail = !isMobile && !isOrderingQuestion && videoThumbnail;

  const thumbnailStyles = showVideoThumbnail
    ? {backgroundImage: `url(${videoThumbnail})`}
    : {padding: 0};

  const Container = editMode ? 'div' : Div100vh;

  const hasImageSpacing =
    !isOrderingQuestion && (imageUrl || quizQuestionsContainImages);

  return (
    <Container
      styleName={className}
      data-automation={`slide${editMode ? '-preview' : ''}${
        isPreview ? '-thumbnail' : ''
      }`}
    >
      {onReplay && (
        <div
          styleName={`thumbnail fade-in${
            showVideoThumbnail ? ' has-thumbnail' : ''
          }`}
          style={thumbnailStyles}
        >
          <button
            onClick={onReplay}
            styleName='replay-button'
            type='button'
            disabled={disabled}
          >
            <span styleName='replay-icon' />
            <Text size='m' colour='white'>
              {replayButtonText}
            </Text>
          </button>
        </div>
      )}
      {hasImageSpacing && (
        <>
          <div styleName='desktop-image fade-in'>
            <ImageQuizImage
              url={imageUrl ? imageUrl : ''}
              thumbnailUrl={thumbnailImageUrl ? thumbnailImageUrl : ''}
              disabled={disabled}
              editMode={editMode}
              imageDataAutomation={`slide-image${
                editMode ? '-desktop-preview' : ''
              }${isPreview ? '-thumbnail' : ''}`}
            />
          </div>
          <div styleName='mobile-image fade-in'>
            <ImageQuizImage
              isPreview={isPreview}
              url={imageUrl ? imageUrl : ''}
              thumbnailUrl={thumbnailImageUrl ? thumbnailImageUrl : ''}
              editMode={editMode}
              imageDataAutomation={`slide-image${editMode ? '-preview' : ''}${
                isPreview ? '-thumbnail' : ''
              }`}
            />
          </div>
        </>
      )}
      <div styleName='question-container'>
        <div styleName='quiz-question' ref={questionContainerRef}>
          <Heading
            as='h2'
            size={hasImageSpacing ? 'hamster' : 'sheep'}
            weight='medium'
            dataAutomation={`quiz-question${isPreview ? '-thumbnail' : ''}`}
            colour={textColour}
          >
            {quizType === 'text' ? instruction : question}
          </Heading>
        </div>
        {quizType !== 'text' && (
          <div styleName='instruction'>
            <Text
              size='m'
              colour={textColour}
              dataAutomation={`instruction-text${editMode ? '-preview' : ''}${
                isPreview ? '-thumbnail' : ''
              }`}
            >
              {instruction}
            </Text>
          </div>
        )}
      </div>
      <div styleName='interaction-content fade-in'>{children}</div>
    </Container>
  );
};

export default CSSModules(InteractiveActivity, styles, {allowMultiple: true});
